/**
 * ConvergedLayout — Unified operator layout for the 5-bucket control plane.
 *
 * Dark sidebar, 5-bucket global nav, section subnav per bucket,
 * demoted secondary access for builder/catalog/docs.
 *
 * Responsive: sidebar collapses to overlay drawer on mobile (<768px).
 * Mobile header with hamburger replaces fixed sidebar.
 */

import { ReactNode, useState, useCallback, useEffect } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { useIsMobile } from '../hooks/useIsMobile';
import { useAuth } from './AuthGate';
import { getAdminToken } from '../services/platform-api';
import './v2/tokens.css';
import './converged-primitives.css';

// ── Build Parity Indicator (sidebar footer) ──────────────────────────────────

declare const __BUILD_COMMIT__: string;
declare const __BUILD_TIME__: string;

function BuildParity() {
  const [apiVersion, setApiVersion] = useState<string | null>(null);
  useEffect(() => {
    fetch(`${import.meta.env.VITE_API_URL || 'https://api-bay-five-33.vercel.app'}/api/info`)
      .then(r => r.ok ? r.json() : null)
      .then(d => setApiVersion(d?.version || null))
      .catch(() => {});
  }, []);

  const commit = typeof __BUILD_COMMIT__ !== 'undefined' ? __BUILD_COMMIT__ : '?';
  const buildAt = typeof __BUILD_TIME__ !== 'undefined' ? __BUILD_TIME__?.slice(0, 10) : '?';

  return (
    <div style={{ fontSize: '0.6rem', color: 'rgba(148,163,184,0.4)', marginTop: 4, lineHeight: 1.4 }}>
      <div>UI: {commit} · {buildAt}</div>
      {apiVersion && <div>API: v{apiVersion}</div>}
    </div>
  );
}

// ── System Health Indicator (sidebar footer) ─────────────────────────────────

const API_URL = import.meta.env.VITE_API_URL || 'https://api-bay-five-33.vercel.app';

function SystemHealthIndicator() {
  const [health, setHealth] = useState<'green' | 'amber' | 'red' | 'unknown'>('unknown');

  useEffect(() => {
    let mounted = true;
    const check = async () => {
      try {
        const res = await fetch(`${API_URL}/health`, { signal: AbortSignal.timeout(5000) });
        if (!mounted) return;
        if (res.ok) { const d = await res.json(); setHealth(d.status === 'healthy' ? 'green' : 'amber'); }
        else setHealth('red');
      } catch { if (mounted) setHealth('red'); }
    };
    check();
    const iv = setInterval(check, 60_000);
    return () => { mounted = false; clearInterval(iv); };
  }, []);

  const config = {
    green:   { color: 'var(--v2-green-500)', label: 'System healthy' },
    amber:   { color: 'var(--v2-amber-500)', label: 'System degraded' },
    red:     { color: 'var(--v2-red-500)',   label: 'System unreachable' },
    unknown: { color: 'var(--v2-text-muted)', label: 'Checking…' },
  }[health];

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
      <span style={{ width: 6, height: 6, borderRadius: '50%', background: config.color, boxShadow: `0 0 6px ${config.color}` }} />
      <span>{config.label}</span>
    </div>
  );
}

// ── Progressive Disclosure (ADR-003 ratified rules) ─────────────────────────
// Sidebar visibility driven by tenant_onboarding_profiles.feature_level.
// Escape hatch: "Show all sections" toggle. Falls back to 'full' on error.

type FeatureLevel = 'essential' | 'standard' | 'advanced' | 'full';

/** Ratified visibility rules from ADR-003.
 *  Builder (/developer) is accessible via "Show all sections" toggle only —
 *  it is not part of the operator wedge path shown to buyers/design partners. */
const VISIBLE_BUCKETS: Record<FeatureLevel, string[]> = {
  essential: ['/control/mission', '/control/oversight-center', '/control/external-agents', '/control/governance'],
  standard:  ['/control/mission', '/control/oversight-center', '/control/external-agents', '/control/governance', '/control/evidence'],
  advanced:  ['/control/mission', '/control/oversight-center', '/control/external-agents', '/control/governance', '/control/evidence', '/control/delegation'],
  full:      ['/control/mission', '/control/oversight-center', '/control/external-agents', '/control/governance', '/control/evidence', '/control/delegation'],
};

function getVisibleNavItems(level: FeatureLevel, showAll: boolean): NavItem[] {
  if (showAll) return primaryNav;
  const allowed = VISIBLE_BUCKETS[level] || VISIBLE_BUCKETS.full;
  return primaryNav.filter(item => allowed.includes(item.to));
}

// ── Global Nav (5 canonical buckets) ─────────────────────────────────────────

interface NavItem {
  to: string;
  label: string;
  icon: string; // SVG path
  badge?: number;
  separator?: boolean; // render a visual divider above this item
}

const primaryNav: NavItem[] = [
  {
    to: '/control/mission',
    label: 'Mission Control',
    icon: 'M3 9l9-7 9 7v11a2 2 0 01-2 2H5a2 2 0 01-2-2z M9 22V12h6v10',
  },
  {
    to: '/control/oversight-center',
    label: 'Review Center',
    icon: 'M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z',
  },
  {
    to: '/control/external-agents',
    label: 'Agents',
    icon: 'M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z',
  },
  {
    to: '/control/governance',
    label: 'Governance',
    icon: 'M3 6l9-4 9 4v4c0 5.523-3.582 10.34-9 12-5.418-1.66-9-6.477-9-12V6z',
  },
  {
    to: '/control/evidence',
    label: 'Evidence',
    icon: 'M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2 M9 5a2 2 0 002 2h2a2 2 0 002-2 M9 5a2 2 0 012-2h2a2 2 0 012 2 M9 14l2 2 4-4',
  },
  {
    to: '/control/delegation',
    label: 'Delegation',
    separator: true,
    icon: 'M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101 M10.172 13.828a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1',
  },
  // Builder (/developer) removed from sidebar — accessible via direct URL only.
  // Not part of the operator/buyer wedge path.
];

// ── Section Sub-Nav Config ───────────────────────────────────────────────────

export interface SectionNavItem {
  to: string;
  label: string;
}

const sectionNavConfig: Record<string, SectionNavItem[]> = {
  '/control/mission': [], // MC uses internal mode tabs
  '/control/oversight-center': [], // Review Center — top-level, no section nav
  '/control/external-agents': [], // Agents — top-level, no section nav
  '/control/delegation': [],
  '/control/coordination': [],
  '/control/governance': [
    { to: '/control/governance', label: 'Overview' },
    { to: '/control/trust-ops', label: 'Trust Operations' },
    { to: '/control/credentials', label: 'Credentials' },
    { to: '/control/intervention', label: 'Intervention' },
    // Verification, Reviews (Recommendations), Boundaries removed from buyer nav —
    // immature or admin-internal surfaces. Routes still accessible via direct URL.
  ],
  '/control/evidence': [
    { to: '/control/evidence', label: 'Overview' },
    { to: '/control/executions', label: 'Executions' },
    { to: '/control/agent-decisions', label: 'Decisions' },
    // Decision Analytics and Trust History removed from buyer nav —
    // analytics surfaces may render broken states with sparse data.
    // Routes still accessible via direct URL.
  ],
  // Builder sub-nav removed — not in sidebar.
  '/developer': [],
};

// ── Helpers ──────────────────────────────────────────────────────────────────

function getBucket(pathname: string): string {
  // Match the current path to its parent bucket
  for (const bucket of Object.keys(sectionNavConfig)) {
    if (pathname === bucket || pathname.startsWith(bucket + '/')) return bucket;
  }
  // Check subordinate routes → parent bucket
  const subordinateMap: Record<string, string> = {
    // Agents bucket
    '/control/objects': '/control/external-agents',
    '/control/overview': '/control/external-agents',
    '/control/agent-messages': '/control/external-agents',
    '/control/selfreg-queue': '/control/external-agents',
    '/control/onboarding': '/control/external-agents',
    '/control/bootstrap-tokens': '/control/external-agents',
    '/control/quick-connect': '/control/external-agents',
    // Delegation bucket
    '/control/delegations': '/control/delegation',
    '/control/coordination': '/control/delegation',
    '/control/sessions': '/control/delegation',
    // Governance bucket
    '/control/trust-ops': '/control/governance',
    '/control/credentials': '/control/governance',
    // Review Center is now top-level (no longer under Governance)
    '/control/intervention': '/control/governance',
    '/control/boundaries': '/control/governance',
    '/control/verification': '/control/governance',
    '/control/recommendations': '/control/governance',
    '/control/domain-packs': '/control/governance',
    '/control/persona-overlays': '/control/governance',
    '/control/tenant-profiles': '/control/governance',
    '/control/outcome-review': '/control/governance',
    '/control/observe-artifacts': '/control/governance',
    '/control/release-packets': '/control/governance',
    // Evidence bucket
    '/control/agent-decisions': '/control/evidence',
    '/control/executions': '/control/evidence',
    '/control/correlation': '/control/evidence',
    '/control/analytics': '/control/evidence',
    '/control/governance-analytics': '/control/evidence',
    '/control/intelligence': '/control/evidence',
    // Mission Control subordinates
    '/control/intake': '/control/mission',
    '/control/getting-started': '/control/mission',
    '/control/readiness': '/control/mission',
    // Builder workspace subordinates
    '/developer/capabilities': '/developer',
    '/developer/workflows': '/developer',
    '/developer/runs': '/developer',
    '/developer/library': '/developer',
    '/developer/orchestration': '/developer',
  };
  for (const [prefix, bucket] of Object.entries(subordinateMap)) {
    if (pathname === prefix || pathname.startsWith(prefix + '/')) return bucket;
  }
  // Fallback path matching
  if (pathname.startsWith('/developer')) return '/developer';
  if (pathname.startsWith('/control/')) return '/control/external-agents';
  return '/control/mission';
}

function isNavActive(pathname: string, itemTo: string): boolean {
  const bucket = getBucket(pathname);
  return bucket === itemTo;
}

function getBucketLabel(pathname: string): string {
  const bucket = getBucket(pathname);
  const item = primaryNav.find(n => n.to === bucket);
  return item?.label || 'Control';
}

// ── Sidebar Link ─────────────────────────────────────────────────────────────

// OPERATOR-CONTROL-DISCOVERABILITY-1 — curated entry-point group.
// Renders a small "Operator Control" header above quick-access links
// to the operator surfaces. Clicking any link drops the operator into
// the v2 shell (OpsLayout) where sibling navigation is available.
const OPERATOR_CONTROL_LINKS: { to: string; label: string }[] = [
  { to: '/control/mission',  label: 'Mission Control' },
  { to: '/v2/agents',        label: 'Agents' },
  { to: '/v2/ops/audit',     label: 'Audit Trail' },
  { to: '/v2/ops/chains',    label: 'Operator Review' },
  // DISCOVERY-1 scaffolding — restore navigation to kill-switch / policy-override page; review during DASHBOARD-CONVERGENCE-1
  { to: '/control/intervention',         label: 'Intervention' },
  // DISCOVERY-1 scaffolding — temporary V2 ops capability discoverability; absorb into canonical nav during DASHBOARD-CONVERGENCE-1
  { to: '/v2/ops/warrants',              label: 'Warrants' },
  { to: '/v2/ops/findings',              label: 'Findings' },
  { to: '/v2/ops/enforcement-shadow',    label: 'Enforcement Shadow' },
];

function OperatorControlGroup({ onClick }: { onClick?: () => void }) {
  const location = useLocation();
  return (
    <div data-testid="converged-operator-control-group" style={{ marginBottom: 4 }}>
      <div
        style={{
          padding: '6px 16px 4px',
          fontSize: 'var(--v2-fs-xs)',
          fontWeight: 600,
          textTransform: 'uppercase',
          letterSpacing: 0.6,
          color: 'rgba(255,255,255,0.45)',
        }}
      >
        Operator Control
      </div>
      {OPERATOR_CONTROL_LINKS.map((link) => {
        const active = location.pathname === link.to || location.pathname.startsWith(link.to + '/');
        return (
          <NavLink
            key={link.to}
            to={link.to}
            onClick={onClick}
            data-testid={`converged-operator-control-link-${link.label.toLowerCase().replace(/\s+/g, '-')}`}
            style={{
              display: 'block',
              padding: '8px 16px 8px 28px',
              borderRadius: 'var(--v2-radius-md)',
              textDecoration: 'none',
              fontSize: 'var(--v2-fs-sm)',
              fontWeight: active ? 600 : 500,
              color: active ? 'var(--v2-text-sidebar-active)' : 'var(--v2-text-sidebar)',
              background: active ? 'var(--v2-bg-sidebar-active)' : 'transparent',
              transition: 'var(--v2-transition-fast)',
            }}
            onMouseEnter={(e) => { if (!active) (e.currentTarget as HTMLElement).style.background = 'var(--v2-bg-sidebar-hover)'; }}
            onMouseLeave={(e) => { if (!active) (e.currentTarget as HTMLElement).style.background = 'transparent'; }}
          >
            {link.label}
          </NavLink>
        );
      })}
    </div>
  );
}

function SidebarLink({ item, onClick }: { item: NavItem; onClick?: () => void }) {
  const location = useLocation();
  const active = isNavActive(location.pathname, item.to);

  return (
    <NavLink
      to={item.to}
      onClick={onClick}
      style={{
        display: 'flex', alignItems: 'center', gap: 12,
        padding: '10px 16px', borderRadius: 'var(--v2-radius-md)',
        textDecoration: 'none', transition: 'var(--v2-transition-fast)',
        color: active ? 'var(--v2-text-sidebar-active)' : 'var(--v2-text-sidebar)',
        background: active ? 'var(--v2-bg-sidebar-active)' : 'transparent',
        fontWeight: active ? 600 : 500,
        fontSize: 'var(--v2-fs-base)',
      }}
      onMouseEnter={(e) => { if (!active) (e.currentTarget as HTMLElement).style.background = 'var(--v2-bg-sidebar-hover)'; }}
      onMouseLeave={(e) => { if (!active) (e.currentTarget as HTMLElement).style.background = 'transparent'; }}
    >
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ opacity: active ? 1 : 0.6, flexShrink: 0 }}>
        {item.icon.split(' M').map((seg, i) => (
          <path key={i} d={i === 0 ? seg : 'M' + seg} />
        ))}
      </svg>
      <span>{item.label}</span>
      {item.badge != null && item.badge > 0 && (
        <span style={{
          marginLeft: 'auto', padding: '1px 7px', borderRadius: 'var(--v2-radius-full)',
          fontSize: 'var(--v2-fs-xs)', fontWeight: 700,
          background: 'var(--v2-indigo-500)', color: '#fff',
          minWidth: 20, textAlign: 'center',
        }}>
          {item.badge}
        </span>
      )}
    </NavLink>
  );
}

// ── Sidebar Content (shared between desktop fixed + mobile overlay) ──────────

function SidebarContent({ onNavClick, featureLevel, showAll }: {
  onNavClick?: () => void;
  featureLevel: FeatureLevel;
  showAll: boolean;
  onToggleShowAll: () => void;
}) {
  const { logout, isAuthenticated } = useAuth();
  const visibleItems = getVisibleNavItems(featureLevel, showAll);

  return (
    <>
      {/* Logo */}
      <div style={{
        padding: '20px 20px 24px',
        borderBottom: '1px solid rgba(255,255,255,0.04)',
      }}>
        <NavLink to="/control/mission" onClick={onNavClick} style={{ display: 'flex', alignItems: 'center', gap: 10, textDecoration: 'none' }}>
          <div style={{
            width: 32, height: 32, borderRadius: 'var(--v2-radius-md)',
            background: 'linear-gradient(135deg, var(--v2-indigo-500), var(--v2-purple-500))',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontWeight: 800, color: '#fff', fontSize: 'var(--v2-fs-lg)',
          }}>
            N
          </div>
          <div>
            <div style={{ color: '#fff', fontWeight: 700, fontSize: 'var(--v2-fs-base)', lineHeight: 1.2 }}>Nexum</div>
            <div style={{ color: 'var(--v2-text-sidebar)', fontSize: 'var(--v2-fs-xs)' }}>Protocol</div>
          </div>
        </NavLink>
      </div>

      {/* Primary Navigation — filtered by feature level */}
      <nav style={{ flex: 1, padding: '12px 12px', display: 'flex', flexDirection: 'column', gap: 2 }}>
        {/* OPERATOR-CONTROL-DISCOVERABILITY-1: curated entry group surfacing
            the v2 operator surfaces that were previously unreachable from
            the main dashboard. This is discoverability, not UX unification —
            the deeper shell fragmentation between ConvergedLayout / OpsLayout /
            V2Layout is deferred to DASHBOARD-UX-UNIFICATION-1. */}
        <OperatorControlGroup onClick={onNavClick} />
        <hr style={{ border: 'none', borderTop: '1px solid rgba(255,255,255,0.06)', margin: '8px 16px' }} />
        {visibleItems.map(item => (
          <div key={item.to}>
            {item.separator && (
              <hr style={{ border: 'none', borderTop: '1px solid rgba(255,255,255,0.06)', margin: '8px 16px' }} />
            )}
            <SidebarLink item={item} onClick={onNavClick} />
          </div>
        ))}
      </nav>

      {/* Footer */}
      <div style={{
        padding: '12px 20px', borderTop: '1px solid rgba(255,255,255,0.04)',
        fontSize: 'var(--v2-fs-xs)', color: 'var(--v2-text-sidebar)',
      }}>
        {/* Show all sections toggle removed — Builder is no longer in the sidebar.
            Toggle was only needed when Builder was hidden behind it. */}
        <SystemHealthIndicator />
        <BuildParity />
        {isAuthenticated && (
          <button
            onClick={() => { logout(); window.location.href = '/'; }}
            style={{
              display: 'block', width: '100%', padding: '8px 0', marginTop: 8,
              background: 'none', border: 'none', color: 'rgba(161,161,170,0.5)',
              fontSize: 'var(--v2-fs-xs)', cursor: 'pointer', textAlign: 'left',
              fontFamily: 'var(--v2-font)',
            }}
            onMouseEnter={(e) => { (e.currentTarget as HTMLElement).style.color = 'var(--v2-text-sidebar)'; }}
            onMouseLeave={(e) => { (e.currentTarget as HTMLElement).style.color = 'rgba(161,161,170,0.5)'; }}
          >
            Sign Out
          </button>
        )}
      </div>
    </>
  );
}

// ── Section Sub-Nav ──────────────────────────────────────────────────────────

export function SectionNav({ items }: { items?: SectionNavItem[] }) {
  const location = useLocation();
  const bucket = getBucket(location.pathname);
  const navItems = items || sectionNavConfig[bucket] || [];

  if (navItems.length === 0) return null;

  return (
    <div style={{
      display: 'flex', gap: 4, padding: '0 16px',
      borderBottom: '1px solid var(--v2-border)',
      background: 'var(--v2-bg-surface)',
      overflowX: 'auto', WebkitOverflowScrolling: 'touch',
    }}>
      {navItems.map(item => {
        const active = location.pathname === item.to
          || (item.to !== bucket && location.pathname.startsWith(item.to + '/'));
        return (
          <NavLink
            key={item.to}
            to={item.to}
            style={{
              padding: '10px 16px',
              fontSize: 'var(--v2-fs-sm)', fontWeight: active ? 700 : 500,
              color: active ? 'var(--v2-indigo-600)' : 'var(--v2-text-muted)',
              borderBottom: active ? '2px solid var(--v2-indigo-500)' : '2px solid transparent',
              textDecoration: 'none', transition: 'var(--v2-transition-fast)',
              fontFamily: 'var(--v2-font)',
              whiteSpace: 'nowrap',
            }}
          >
            {item.label}
          </NavLink>
        );
      })}
    </div>
  );
}

// ── Page Header ──────────────────────────────────────────────────────────────

export function PageHeader({ title, subtitle, actions }: { title: string; subtitle?: string; actions?: ReactNode }) {
  return (
    <div style={{
      padding: '24px 16px 20px',
      borderBottom: '1px solid var(--v2-border)',
      background: 'var(--v2-bg-surface)',
      display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between',
      flexWrap: 'wrap', gap: 8,
    }}>
      <div style={{ minWidth: 0 }}>
        <h1 style={{ fontSize: 'var(--v2-fs-xl)', fontWeight: 700, color: 'var(--v2-text-primary)', margin: 0, lineHeight: 1.3, fontFamily: 'var(--v2-font)' }}>{title}</h1>
        {subtitle && <p style={{ fontSize: 'var(--v2-fs-md)', color: 'var(--v2-text-muted)', margin: '4px 0 0', fontFamily: 'var(--v2-font)' }}>{subtitle}</p>}
      </div>
      {actions && <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>{actions}</div>}
    </div>
  );
}

// ── Page Content ─────────────────────────────────────────────────────────────

export function PageContent({ children }: { children: ReactNode }) {
  return (
    <div className="page-content" style={{ padding: '24px 16px' }}>
      {children}
    </div>
  );
}

// ── Mobile Header ────────────────────────────────────────────────────────────

function MobileHeader({ onMenuClick }: { onMenuClick: () => void }) {
  const location = useLocation();
  const label = getBucketLabel(location.pathname);

  return (
    <div style={{
      display: 'flex', alignItems: 'center',
      height: 48, padding: '0 12px',
      background: 'var(--v2-bg-sidebar, #0f172a)',
      color: '#fff',
      position: 'sticky', top: 0, zIndex: 50,
      gap: 12,
    }}>
      <button
        onClick={onMenuClick}
        aria-label="Open menu"
        style={{
          background: 'none', border: 'none', color: '#fff',
          padding: 8, margin: -8, cursor: 'pointer',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          minWidth: 44, minHeight: 44,
        }}
      >
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M3 12h18M3 6h18M3 18h18" />
        </svg>
      </button>
      <span style={{ fontWeight: 600, fontSize: 'var(--v2-fs-base)', fontFamily: 'var(--v2-font)' }}>{label}</span>
    </div>
  );
}

// ── Main Layout ──────────────────────────────────────────────────────────────

export function ConvergedLayout({ children }: { children: ReactNode }) {
  const isMobile = useIsMobile();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [featureLevel, setFeatureLevel] = useState<FeatureLevel>('full');
  const [showAll, setShowAll] = useState(false);

  // Fetch tenant profile for progressive disclosure
  const fetchProfile = useCallback(() => {
    const token = getAdminToken();
    if (!token) return;
    const BASE = import.meta.env.VITE_API_URL || 'https://api-bay-five-33.vercel.app';
    fetch(`${BASE}/api/v1/onboarding/profile`, {
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
    })
      .then(r => r.ok ? r.json() : null)
      .then(data => {
        if (data?.feature_level && ['essential', 'standard', 'advanced', 'full'].includes(data.feature_level)) {
          setFeatureLevel(data.feature_level as FeatureLevel);
        }
      })
      .catch(() => { /* fall back to 'full' — no hard wall */ });
  }, []);

  useEffect(() => { fetchProfile(); }, [fetchProfile]);

  // Listen for profile changes from GovernanceOverview (instant sidebar update)
  useEffect(() => {
    const handler = (e: StorageEvent) => {
      if (e.key === 'nexum_feature_level' && e.newValue) {
        const level = e.newValue as FeatureLevel;
        if (['essential', 'standard', 'advanced', 'full'].includes(level)) {
          setFeatureLevel(level);
        }
      }
    };
    window.addEventListener('storage', handler);
    return () => window.removeEventListener('storage', handler);
  }, []);

  const closeSidebar = useCallback(() => setSidebarOpen(false), []);
  const toggleShowAll = useCallback(() => setShowAll(prev => !prev), []);

  return (
    <div className="converged-root" style={{
      display: 'flex', minHeight: '100vh',
      fontFamily: 'var(--v2-font)',
      background: 'var(--v2-bg-page)',
      color: 'var(--v2-text-primary)',
    }}>
      {isMobile ? (
        <>
          {/* Mobile: overlay sidebar */}
          {sidebarOpen && (
            <>
              {/* Backdrop */}
              <div
                onClick={closeSidebar}
                style={{
                  position: 'fixed', inset: 0, zIndex: 99,
                  background: 'rgba(0,0,0,0.5)',
                }}
              />
              {/* Sidebar drawer */}
              <aside style={{
                width: 'var(--v2-sidebar-width)', flexShrink: 0,
                background: 'var(--v2-bg-sidebar)',
                display: 'flex', flexDirection: 'column',
                position: 'fixed', top: 0, bottom: 0, left: 0,
                zIndex: 100,
                boxShadow: '4px 0 24px rgba(0,0,0,0.3)',
              }}>
                <SidebarContent onNavClick={closeSidebar} featureLevel={featureLevel} showAll={showAll} onToggleShowAll={toggleShowAll} />
              </aside>
            </>
          )}

          {/* Main content (full width on mobile) */}
          <div style={{
            flex: 1, minHeight: '100vh', width: '100%',
            display: 'flex', flexDirection: 'column',
            overflowX: 'hidden',
          }}>
            <MobileHeader onMenuClick={() => setSidebarOpen(true)} />
            <SectionNav />
            <main style={{ flex: 1, overflowX: 'hidden' }}>
              {children}
            </main>
          </div>
        </>
      ) : (
        <>
          {/* Desktop: fixed sidebar */}
          <aside style={{
            width: 'var(--v2-sidebar-width)', flexShrink: 0,
            background: 'var(--v2-bg-sidebar)',
            display: 'flex', flexDirection: 'column',
            borderRight: '1px solid rgba(255,255,255,0.04)',
            position: 'fixed', top: 0, bottom: 0, left: 0,
            zIndex: 50,
          }}>
            <SidebarContent featureLevel={featureLevel} showAll={showAll} onToggleShowAll={toggleShowAll} />
          </aside>

          {/* Main content */}
          <div style={{
            flex: 1, marginLeft: 'var(--v2-sidebar-width)',
            minHeight: '100vh',
            display: 'flex', flexDirection: 'column',
          }}>
            <SectionNav />
            <main style={{ flex: 1 }}>
              {children}
            </main>
          </div>
        </>
      )}
    </div>
  );
}
